import dayjs from 'dayjs';
import { isWithinInterval } from 'date-fns';
import { format } from 'date-fns';
import { SHIFT_STATUS, SHIFT_STATUS_COLORS } from './constants/shiftConstants';
import { formatMsToHoursAndMinutes } from './formatting/timeFormatting';
import theme from '../theme';
import { MIN_SHIFT_DURATION } from './constants/shiftConstants';

/**
 * Function: calculateTimeUntilShift
 * @description Calculates the time remaining until a shift starts and returns it as a formatted string (e.g., "in 2h 15m").
 * @param {string|Date} start - The start time of the shift, provided as a string or `Date` object.
 * @returns {string} - A formatted string indicating the time remaining until the shift starts (e.g., "in 2h 15m"). Returns an empty string if the start time is invalid or the shift has already started.
 */
export const calculateTimeUntilShift = (start) => {
  if (!start) return '';

  const now = new Date();
  const shiftStart = new Date(start);
  const difference = shiftStart - now;

  if (difference <= 0) return '';

  const hours = Math.floor(difference / (1000 * 60 * 60));
  const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));

  if (hours > 0) {
    return `in ${hours}h ${minutes}m`;
  } else {
    return `in ${minutes}m`;
  }
};

/**
 * Function: calculateShiftHours
 * @description Calculates the total hours of a shift based on its start and end times.
 * @param {Object} shift - The shift object containing `start` and `end` properties as timestamps.
 * @returns {string} - A formatted string indicating the total hours of the shift (e.g., "8h shift").
 */
export const calculateShiftHours = (shift) => {
  const start = new Date(shift.start);
  const end = new Date(shift.end);
  const totalHours = Math.floor((end - start) / (1000 * 60 * 60));
  return `${totalHours}h shift`;
};

/**
 * Function: getShiftStatus
 * @description Determines the current status of a shift based on its timing and clock-in/clock-out data.
 * @param {Object} shift - The shift object containing `start`, `end`, `clockedInAt`, and `clockedOutAt` as timestamps.
 * @returns {string} - The shift status: 'scheduled', 'in_progress', 'late', 'missed', or 'completed'.
 */
export const getShiftStatus = (shift) => {
  // Fast path: Check clock status first as it's a simple object property check
  if (shift.clockedInAt) {
    if (!shift.clockedOutAt) {
      // Check for active breaks
      if (shift.breaks?.length > 0) {
        const activeBreak = shift.breaks.find((b) => b.start && !b.end);
        if (activeBreak) {
          // Check if it's a meal break (could be 'MEAL_BREAK' or 'MEAL')
          const isMealBreak = activeBreak?.name?.toUpperCase().includes('MEAL');
          return isMealBreak ? SHIFT_STATUS.ON_BREAK : SHIFT_STATUS.ON_REST;
        }
      }
      return SHIFT_STATUS.IN_PROGRESS;
    }
    return SHIFT_STATUS.COMPLETED;
  }

  // Only create Date objects if we need them for time comparison
  const now = new Date().getTime();
  const scheduledStart = new Date(shift.start).getTime();

  // Check if shift hasn't started yet
  if (now < scheduledStart) {
    return SHIFT_STATUS.SCHEDULED;
  }

  // Check for missed shift
  const scheduledEnd = new Date(shift.end).getTime();
  if (now > scheduledEnd) {
    return SHIFT_STATUS.MISSED;
  }

  // Check if shift is late
  if (now > scheduledStart) {
    return SHIFT_STATUS.LATE;
  }

  // Default to scheduled if no other conditions match
  return SHIFT_STATUS.SCHEDULED;
};

/**
 * Function: calculateLateTime
 * @description Calculates how late a shift has started and returns it as a formatted string (e.g., "2 hours and 15 minutes").
 * @param {string|Date} start - The scheduled start time of the shift, provided as a string or `Date` object.
 * @returns {string} - A formatted string indicating the late time or an empty string if the start time is invalid.
 */
export const calculateLateTime = (start) => {
  if (!start) return '';

  const now = new Date();
  const shiftStart = new Date(start);
  const difference = now - shiftStart;

  const hours = Math.floor(difference / (1000 * 60 * 60));
  const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));

  if (hours > 0) {
    return `${hours} hour${hours > 1 ? 's' : ''}${minutes > 0 ? ` and ${minutes} minute${minutes > 1 ? 's' : ''}` : ''}`;
  } else {
    return `${minutes} minute${minutes > 1 ? 's' : ''}`;
  }
};

/**
 * Function: getDailyCOLStatus (get daily cost of labor status)
 * @description Generates a subtitle and color for admin view showing labor cost percentages based on shift timing.
 * For past and current days, it shows the actual cost percentage.
 * For future days, it shows the projected cost percentage.
 *
 * @param {boolean} isToday - Indicates if the current day is today
 * @param {boolean} isPast - Indicates if the date is in the past
 * @param {Object} dailyTicket - The daily ticket object containing costPercentage, projectedCostPercentage, costColor, and projectedCostColor
 * @returns {{message: string, color: string}} - An object containing the message and color
 */
export const getDailyCOLStatus = (isToday, isPast, dailyTicket) => {
  const costPercentage = dailyTicket?.costPercentage || 0;
  const projectedCostPercentage = dailyTicket?.projectedCostPercentage || 0;
  const costColor = dailyTicket?.costColor;
  const projectedCostColor = dailyTicket?.projectedCostColor;

  let message;
  if (isToday) {
    message = `Currently ${costPercentage}% cost of labor`;
  } else if (isPast) {
    message = `${costPercentage}% cost of labor`;
  } else {
    message = `Forecasted ${projectedCostPercentage}% cost of labor`;
  }

  const color = isToday || isPast ? costColor : projectedCostColor;

  return {
    message,
    color: color || theme.colors.gray,
  };
};

/**
 * Function: getDailyShiftsStatus
 * @description Generates detailed text and color based on the shift statuses for a specific day, including logged hours, missed shifts, in-progress shifts, and upcoming shifts.
 * @param {Object} params - The parameters object.
 * @param {boolean} params.isToday - Indicates if the current day is today.
 * @param {boolean} params.isPast - Indicates if the date is in the past.
 * @param {Array} params.dayShifts - An array of shift objects for the day.
 * @param {number} params.futureShiftsCount - The number of future shifts scheduled for the day.
 * @returns {{message: string, color: string}} - An object containing the message and color
 */
export const getDailyShiftsStatus = ({ isToday, isPast, dayShifts = [], futureShiftsCount = 0 }) => {
  // Initialize variables to track completed hours, missed shifts, in-progress shifts, and upcoming shifts
  let totalMillisecondsLogged = 0;
  let hasMissedShift = false;
  let hasInProgressShift = false;
  let hasScheduledShift = false;
  let nextShiftStartTime = null;
  let isLate = false;

  // Iterate through each shift for the day
  dayShifts.forEach((shift) => {
    const shiftStatus = getShiftStatus(shift);
    const shiftStart = new Date(shift.start);

    if (shiftStatus === SHIFT_STATUS.COMPLETED) {
      const startTime = new Date(shift.clockedInAt);
      const endTime = new Date(shift.clockedOutAt);
      totalMillisecondsLogged += endTime - startTime; // Calculate total time logged in milliseconds
    } else if (shiftStatus === SHIFT_STATUS.MISSED) {
      hasMissedShift = true;
    } else if (shiftStatus === SHIFT_STATUS.IN_PROGRESS) {
      hasInProgressShift = true;
    } else if (shiftStatus === SHIFT_STATUS.SCHEDULED) {
      hasScheduledShift = true;
      if (!nextShiftStartTime || shiftStart < nextShiftStartTime) {
        nextShiftStartTime = shiftStart; // Track the next scheduled shift start time
      }

      // Check if the employee is late for the shift
      if (shiftStart < new Date()) {
        isLate = true;
      }
    }
  });

  let message;
  let color;

  // Determine the message and color based on the shift statuses
  if (isToday) {
    if (totalMillisecondsLogged > 0) {
      message = `${formatMsToHoursAndMinutes(totalMillisecondsLogged)} logged`;
      color = theme.colors[SHIFT_STATUS_COLORS[SHIFT_STATUS.COMPLETED]];
    } else if (hasInProgressShift) {
      message = 'Shift in progress';
      color = theme.colors[SHIFT_STATUS_COLORS[SHIFT_STATUS.IN_PROGRESS]];
    } else if (isLate) {
      message = 'You are late for the shift';
      color = theme.colors[SHIFT_STATUS_COLORS[SHIFT_STATUS.LATE]];
    } else if (hasScheduledShift && nextShiftStartTime) {
      message = `Shift starts in ${calculateTimeUntilShift(nextShiftStartTime)}`;
      color = theme.colors[SHIFT_STATUS_COLORS[SHIFT_STATUS.SCHEDULED]];
    } else if (hasMissedShift) {
      message = 'Missed shift';
      color = theme.colors[SHIFT_STATUS_COLORS[SHIFT_STATUS.MISSED]];
    } else {
      message = 'Not scheduled';
      color = theme.colors[SHIFT_STATUS_COLORS.NOT_SCHEDULED];
    }
  } else if (!isPast) {
    // Future date
    message = futureShiftsCount > 0 ? `${futureShiftsCount} shift${futureShiftsCount > 1 ? 's' : ''}` : 'Not scheduled';
    color =
      futureShiftsCount > 0
        ? theme.colors[SHIFT_STATUS_COLORS[SHIFT_STATUS.SCHEDULED]]
        : theme.colors[SHIFT_STATUS_COLORS.NOT_SCHEDULED];
  } else {
    // Past date
    if (totalMillisecondsLogged > 0) {
      message = `${formatMsToHoursAndMinutes(totalMillisecondsLogged)} logged`;
      color = theme.colors[SHIFT_STATUS_COLORS[SHIFT_STATUS.COMPLETED]];
    } else if (hasInProgressShift) {
      message = 'Shift in progress';
      color = theme.colors[SHIFT_STATUS_COLORS[SHIFT_STATUS.IN_PROGRESS]];
    } else if (hasMissedShift) {
      message = 'Missed shift';
      color = theme.colors[SHIFT_STATUS_COLORS[SHIFT_STATUS.MISSED]];
    } else {
      message = 'Not scheduled';
      color = theme.colors[SHIFT_STATUS_COLORS.NOT_SCHEDULED];
    }
  }

  return { message, color };
};

/**
 * Function: getNextUpcomingShift
 * @description Finds and returns the next upcoming shift from a list of shifts based on the current time.
 * @param {Array} shifts - An array of shift objects containing `start` timestamps.
 * @returns {Object|null} - The next upcoming shift object, or null if no upcoming shift exists.
 */
export const getNextUpcomingShift = (shifts) => {
  const now = new Date();
  return shifts.find((shift) => new Date(shift.start) > now) || null;
};

/**
 * Function: calculateTotalHoursAndOvertime
 * @description Calculates the total hours worked and overtime hours for shifts within a given week range.
 * @param {Array} shifts - An array of shift objects containing `start` and `end` timestamps.
 * @param {Object} startOfWeek - A `dayjs` object representing the start of the week.
 * @param {Object} endOfWeek - A `dayjs` object representing the end of the week.
 * @returns {Object} - An object containing `totalHours` (float) and `overtimeHours` (float).
 */
// !TODO: Probably need tweaking, needs review.
export const calculateTotalHoursAndOvertime = (shifts, startOfWeek, endOfWeek) => {
  // Filter shifts to only include those within the current week range
  const weekShifts = shifts.filter((shift) => {
    const shiftDate = dayjs(shift.start);
    return shiftDate.isAfter(startOfWeek) && shiftDate.isBefore(endOfWeek);
  });

  // Calculate the difference between start and end of each shift
  const totalHours = weekShifts.reduce((acc, shift) => {
    const start = dayjs(shift.start);
    const end = dayjs(shift.end);
    const hours = end.diff(start, 'hour', true); // Calculate hours as a float (e.g., 8.5 for 8 hours and 30 minutes)
    return acc + hours;
  }, 0);

  // Overtime is any hours over 40 in a week
  const overtimeHours = totalHours > 40 ? totalHours - 40 : 0;

  return { totalHours, overtimeHours };
};

/**
 * Function: isShiftInSelectedWeek
 * @description Checks if a given shift falls within the selected week range.
 * @param {Object} shift - The shift object containing a `start` timestamp.
 * @param {Date} startOfWeek - The start date of the selected week.
 * @param {Date} endOfWeek - The end date of the selected week.
 * @returns {boolean} - `true` if the shift is within the week range, `false` otherwise.
 */
export const isShiftInSelectedWeek = (shift, startOfWeek, endOfWeek) =>
  isWithinInterval(new Date(shift.start), {
    start: startOfWeek,
    end: endOfWeek,
  });

/**
 * Function: wouldTimeCauseOverlap
 * @description Checks if selecting a given start or end time would cause an overlap with any existing shifts.
 * @param {Object} timeToCheck - The time to check for overlap, provided as a `Date` object.
 * @param {boolean} isStartTime - A boolean indicating whether the time to check is a start time.
 * @param {Object} shiftFromStorage - The shift from storage object.
 * @param {Object} shiftsByAssignment - The shifts by assignment object.
 * @returns {boolean} - `true` if the time overlaps with any existing shifts, `false` otherwise.
 */
export const wouldTimeCauseOverlap = (timeToCheck, isStartTime, currentShift, shiftsByAssignment) => {
  if (!currentShift?.eid || !shiftsByAssignment?.assigned) return false;

  // Extract the day from timeToCheck to compare with existing shifts
  const checkDateString = timeToCheck.toDateString();

  return Object.values(shiftsByAssignment.assigned)
    .flat()
    .some((employeeShift) => {
      // Ignore the current shift itself
      if (employeeShift.id === currentShift.id) return false;
      // Only compare shifts for the same employee
      if (employeeShift.eid !== currentShift.eid) return false;

      const shiftStart = new Date(employeeShift.start);
      const shiftEnd = new Date(employeeShift.end);

      // Ensure we're only checking shifts on the same day
      if (shiftStart.toDateString() !== checkDateString) return false;

      const timeToCheckMs = timeToCheck.getTime();

      if (isStartTime) {
        const potentialEnd = currentShift?.end ? new Date(currentShift.end).getTime() : timeToCheckMs + 60 * 60 * 1000; // Default to 1-hour shift if no end time is set

        return (
          (timeToCheckMs >= shiftStart.getTime() && timeToCheckMs < shiftEnd.getTime()) || // Check if new shift starts within existing shift
          (potentialEnd > shiftStart.getTime() && timeToCheckMs < shiftEnd.getTime()) || // Check if new shift ends within existing shift
          (timeToCheckMs <= shiftStart.getTime() && potentialEnd >= shiftEnd.getTime()) // Check if new shift fully contains an existing shift
        );
      } else {
        const currentStart = currentShift?.start ? new Date(currentShift.start).getTime() : timeToCheckMs;

        return (
          (timeToCheckMs > shiftStart.getTime() && timeToCheckMs <= shiftEnd.getTime()) || // Check if new shift starts within existing shift
          (currentStart < shiftEnd.getTime() && timeToCheckMs > shiftStart.getTime()) || // Check if new shift ends within existing shift
          (currentStart <= shiftStart.getTime() && timeToCheckMs >= shiftEnd.getTime()) // Check if new shift fully contains an existing shift
        );
      }
    });
};

/**
 * Function: isValidShiftDuration
 * @description Checks if the duration of a shift meets the minimum allowed duration.
 * @param {Object} startTime - The start time of the shift, provided as a `Date` object.
 * @param {Object} endTime - The end time of the shift, provided as a `Date` object.
 * @returns {boolean} - `true` if the shift duration is valid, `false` otherwise.
 */
export const isValidShiftDuration = (startTime, endTime) => {
  return new Date(endTime) - new Date(startTime) >= MIN_SHIFT_DURATION;
};

/**
 * Function: hasOverlappingShifts
 * @description Checks if an employee has overlapping shifts.
 * @param {string} employeeId - The ID of the employee to check.
 * @param {Object} shiftsByAssignment - The shifts by assignment object.
 * @param {Object} shiftToCheck - The shift to check for overlaps.
 * @returns {boolean} - `true` if there are overlapping shifts, `false` otherwise.
 */
export const hasOverlappingShifts = (employeeId, shiftToCheck, shiftsByAssignment) => {
  if (!shiftsByAssignment?.assigned || !shiftToCheck) return false;

  const shiftToCheckStart = new Date(shiftToCheck.start).getTime();
  const shiftToCheckEnd = new Date(shiftToCheck.end).getTime();

  return Object.values(shiftsByAssignment.assigned)
    .flat()
    .some((shift) => {
      // Skip the current shift
      if (shift.id === shiftToCheck.id) return false;

      // Only check shifts for this employee
      if (shift.eid !== employeeId) return false;

      const scheduledStart = new Date(shift.start).getTime();
      const scheduledEnd = new Date(shift.end).getTime();
      const clockedInAt = shift.clockedInAt ? new Date(shift.clockedInAt).getTime() : null;
      const clockedOutAt = shift.clockedOutAt ? new Date(shift.clockedOutAt).getTime() : null;

      // If the employee has clocked in but not out, they are currently working
      // Use scheduled end time as the end time since they haven't clocked out yet
      if (clockedInAt && !clockedOutAt) {
        return shiftToCheckStart < scheduledEnd && shiftToCheckEnd > clockedInAt;
      }

      // If the employee has both clocked in and out, use those times
      if (clockedInAt && clockedOutAt) {
        return shiftToCheckStart < clockedOutAt && shiftToCheckEnd > clockedInAt;
      }

      // If no clock times exist, fall back to scheduled times
      return shiftToCheckStart < scheduledEnd && shiftToCheckEnd > scheduledStart;
    });
};

/**
 * Function: calculateScheduledTime
 * @description Calculates the total scheduled time for an array of shifts.
 * @param {Array} shifts - An array of shift objects containing `start` and `end` timestamps.
 * @returns {Object} - An object containing `hours` and `minutes` representing the total scheduled time.
 */
export const calculateScheduledTime = (shifts) => {
  const totalMilliseconds = shifts.reduce((total, shift) => {
    return total + (new Date(shift.end).getTime() - new Date(shift.start).getTime());
  }, 0);

  return {
    hours: Math.floor(totalMilliseconds / (1000 * 60 * 60)),
    minutes: Math.floor((totalMilliseconds % (1000 * 60 * 60)) / (1000 * 60)),
  };
};

/**
 * Function: calculateLoggedTime
 * @description Calculates the total time actually worked based on clock-in and clock-out times.
 * @param {Array} shifts - An array of shift objects containing `clockedInAt` and `clockedOutAt` timestamps.
 * @returns {Object} - An object containing `hours` and `minutes` representing the total logged time.
 */
export const calculateLoggedTime = (shifts) => {
  const totalMilliseconds = shifts.reduce((total, shift) => {
    if (shift.clockedInAt && shift.clockedOutAt) {
      return total + (new Date(shift.clockedOutAt).getTime() - new Date(shift.clockedInAt).getTime());
    }
    return total;
  }, 0);

  return {
    hours: Math.floor(totalMilliseconds / (1000 * 60 * 60)),
    minutes: Math.floor((totalMilliseconds % (1000 * 60 * 60)) / (1000 * 60)),
  };
};

/**
 * Function: getTimeUntilShift
 * @description Calculates the time remaining until a shift starts
 * @param {string|Date} shiftStart - The start time of the shift
 * @returns {Object} Object containing hours and minutes until shift starts
 */
export const getTimeUntilShift = (shiftStart) => {
  const now = new Date();
  const start = new Date(shiftStart);
  const diffMs = start - now;
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

  return { hours, minutes };
};

/**
 * Function: getShiftsForDate
 * @description Filters shifts for a specific date
 * @param {Array} shifts - Array of shift objects
 * @param {Date} date - The date to filter by
 * @returns {Array} Shifts that occur on the specified date
 */
export const getShiftsForDate = (shifts, date) => {
  return shifts.filter((shift) => {
    const shiftDate = new Date(shift.start);
    return format(shiftDate, 'yyyy-MM-dd') === format(date, 'yyyy-MM-dd');
  });
};

/**
 * Function: getUnassignedShiftsForEmployee
 * @description Filters shifts to find unassigned shifts that match an employee's shift types on a specific date
 * @param {Date|string} date - The date to check for unassigned shifts
 * @param {Array} allShifts - Array of all shifts
 * @param {Object} employee - Employee object containing shiftTypes array
 * @returns {Array} Array of unassigned shifts that match the employee's shift types
 */
export const getUnassignedShiftsForEmployee = (date, allShifts, employee) => {
  const shiftDate = new Date(date);

  return allShifts.filter(
    (shift) =>
      new Date(shift.start).toDateString() === shiftDate.toDateString() &&
      !shift.assignedName &&
      employee.shiftTypes?.some((type) => type === shift.name)
  );
};

/**
 * Function: getWeeklyShiftCount
 * @description Calculates number of shifts in a given week
 * @param {Array} shifts - Array of shifts to count
 * @param {number} startOfWeekTimestamp - Unix timestamp for start of week
 * @returns {number} Number of shifts in the week
 */
export const getWeeklyShiftCount = (shifts, startOfWeekTimestamp) => {
  const endOfWeekTimestamp = startOfWeekTimestamp + 7 * 24 * 60 * 60 * 1000;

  return shifts.filter((shift) => {
    const shiftDate = new Date(shift.start);
    return shiftDate >= startOfWeekTimestamp && shiftDate <= new Date(endOfWeekTimestamp);
  }).length;
};

/**
 * Function: getScheduledHoursForDay
 * @description Calculates the total scheduled hours for an array of shifts
 * @param {Array} shifts - An array of shift objects containing start and end timestamps
 * @returns {number} - Total scheduled hours (rounded to 1 decimal place if not whole)
 */
export const getScheduledHoursForDay = (shifts) => {
  if (!shifts || shifts.length === 0) {
    return 0;
  }

  const totalHours = shifts.reduce((total, shift) => {
    const start = new Date(shift.start);
    const end = new Date(shift.end);
    return total + (end - start) / (1000 * 60 * 60);
  }, 0);

  // Return whole number if possible, otherwise one decimal place
  return totalHours % 1 === 0 ? Math.floor(totalHours) : Number(totalHours.toFixed(1));
};

export const shouldShowStartButton = (shift) => {
  if (!shift) return false;

  const currentTime = new Date().getTime();
  const shiftStartTime = new Date(shift.start).getTime();
  const shiftEndTime = new Date(shift.end).getTime();
  const fifteenMinutes = 15 * 60 * 1000;

  //if the shift has ended, don't show the start button
  if (currentTime >= shiftEndTime) return false;

  // should show the start button if:
  // 1. The shift is about to start (within 15 minutes before)
  // 2. The shift has started but hasn't ended (even if it's late)
  const isWithinStartWindow = currentTime >= shiftStartTime - fifteenMinutes && currentTime < shiftStartTime;
  const isShiftNotEnded = currentTime < shiftEndTime;

  return isWithinStartWindow || isShiftNotEnded;
};

export const getNextUpdateDelay = (shift) => {
  if (!shift) return 0;

  const currentTime = new Date().getTime();
  const shiftStartTime = new Date(shift.start).getTime();
  const shiftEndTime = new Date(shift.end).getTime();

  if (currentTime < shiftStartTime) {
    // if the shift hasn't started yet, update when it starts
    return shiftStartTime - currentTime;
  }
  // if the shift has started, update when it ends
  return shiftEndTime - currentTime;
};
